import { useEffect, useMemo, useRef } from 'react';

import { Box, Text, Tooltip } from '@mantine/core';
import { components, OptionProps } from 'react-select';

import { useGetActiveAppointmentsQuery } from '@/store/slices/api/appointmentApiSlice';
import { useListTasksQuery, useListTaskTypesQuery } from '@/store/slices/api/tasksApiSlice';
import { appointmentStatusLabel, appointmentTypeLabel } from '@/utils/appointmentTypeLabel';
import formatDate from '@/utils/formatDate';
import {
  appointmentToPreselect,
  isEligibleDraftAppointment,
  isInClinicAppointment,
  ownTransmissionTask
} from '@/utils/reportAppointmentEligibility';

import { LabelAndValue } from '@/types/custom';
import { Appointment } from '@/types/matchDevice';
import Dropdown from '@/ui/Dropdown';

type AppointmentOption = LabelAndValue & {
  isDisabled?: boolean;
  tooltip?: string;
};

const CustomOption = (props: OptionProps<AppointmentOption, false>) => {
  const { data, children, ...rest } = props;

  if (data.isDisabled) {
    return (
      <Tooltip
        position="right-end"
        zIndex={1000}
        label={data.tooltip || 'This appointment already has a transmission associated with it'}>
        <Box>
          <components.Option {...rest} data={data} isDisabled={true}>
            <Text c="dimmed" fz="sm" td="line-through" fw={500}>
              {children}
            </Text>
          </components.Option>
        </Box>
      </Tooltip>
    );
  }

  return <components.Option {...props} />;
};

export const AppointmentSelection = ({
  reportId,
  patientId,
  deviceId,
  appointment,
  savedAppointmentId,
  handleChange,
  onEligibleAppointments,
  onAppointmentLocked,
  onCurrentAppointmentEncounter,
  isDisabled,
  inClinicOnly = false,
  requireSelection = true
}: {
  reportId: number;
  patientId: string;
  deviceId: string;
  appointment: Appointment | null;
  savedAppointmentId?: string | null;
  isDisabled: boolean;
  inClinicOnly?: boolean;
  requireSelection?: boolean;
  handleChange: (option: LabelAndValue | null, source?: Appointment | null) => void;
  onEligibleAppointments?: (appointments: Appointment[]) => void;
  onAppointmentLocked?: (locked: boolean) => void;
  onCurrentAppointmentEncounter?: (encounterId?: string | null) => void;
}) => {
  const { data: appointments, isLoading } = useGetActiveAppointmentsQuery(
    { deviceId },
    {
      skip: isDisabled
    }
  );
  const { data: taskTypes, isSuccess: taskTypesLoaded } = useListTaskTypesQuery(undefined, {
    skip: isDisabled
  });
  const transmissionTaskTypeId = taskTypes?.find(
    (type) => type.workType === 'clinical' && type.code === 'transmission_tracking'
  )?.id;
  const { data: transmissionTasks } = useListTasksQuery(
    { patientId, taskTypeIds: [transmissionTaskTypeId ?? ''], limit: 100 },
    { skip: isDisabled || !patientId || !transmissionTaskTypeId }
  );
  const getAppointmentName = (item: {
    type?: string | null;
    scheduledDate?: string | Date | null;
    status?: string | null;
  }) => {
    return `${appointmentTypeLabel(item.type)} - ${formatDate(item.scheduledDate)} (${appointmentStatusLabel(item.status)})`;
  };

  const rememberedAppointment = useRef<Appointment | null>(appointment);
  if (appointment?.type || appointment?.scheduledDate || appointment?.status) {
    rememberedAppointment.current = appointment;
  } else if (rememberedAppointment.current?.id !== appointment?.id) {
    rememberedAppointment.current = appointment;
  }

  const selectableAppointments = useMemo(() => {
    if (!appointments) return [];
    return appointments.filter((item) => {
      const linkedId = item.linkedTransmissionReport?.id;
      const linkedToThisReport = linkedId != null && String(linkedId) === String(reportId);
      if (item.id === appointment?.id || item.id === savedAppointmentId || linkedToThisReport) {
        return true;
      }
      if (inClinicOnly && !isInClinicAppointment(item)) return false;
      return isEligibleDraftAppointment(item, reportId);
    });
  }, [appointments, appointment?.id, inClinicOnly, reportId, savedAppointmentId]);

  const options = useMemo((): AppointmentOption[] => {
    const fromList = appointments?.find((item) => item.id === appointment?.id);
    if (fromList) rememberedAppointment.current = fromList;

    const appointmentOptions = selectableAppointments.map((item) => ({
      value: item.id,
      label: getAppointmentName(item)
    }));
    const current = rememberedAppointment.current;
    if (current?.id && !appointmentOptions.some((option) => option.value === current.id)) {
      appointmentOptions.unshift({
        value: current.id,
        label: getAppointmentName(current)
      });
    }

    if (!appointments || isLoading) return appointmentOptions;
    return [{ value: '', label: '-' }, ...appointmentOptions];
  }, [appointment?.id, appointments, isLoading, selectableAppointments]);

  useEffect(() => {
    if (!appointments) return;
    onEligibleAppointments?.(selectableAppointments);
  }, [appointments, onEligibleAppointments, selectableAppointments]);

  const didPreselect = useRef(false);
  const lastPreselectReportId = useRef(reportId);
  if (lastPreselectReportId.current !== reportId) {
    lastPreselectReportId.current = reportId;
    didPreselect.current = false;
  }
  const ownTask = ownTransmissionTask(transmissionTasks?.items ?? [], reportId);
  const ownTaskAppointment = appointments?.find((item) => item.id === ownTask?.appointmentId);
  const lockedToTask = Boolean(
    ownTask?.appointmentId &&
      (!inClinicOnly || (ownTaskAppointment && isInClinicAppointment(ownTaskAppointment)))
  );

  useEffect(() => {
    onAppointmentLocked?.(lockedToTask);
  }, [lockedToTask, onAppointmentLocked]);

  useEffect(() => {
    if (isDisabled || isLoading || !appointments || didPreselect.current) return;
    if (!taskTypesLoaded) return;
    if (transmissionTaskTypeId && !transmissionTasks) return;
    didPreselect.current = true;
    const currentId = savedAppointmentId || appointment?.id;
    if (currentId) {
      const current = appointments.find((item) => item.id === currentId);
      onCurrentAppointmentEncounter?.(current ? current.emrEncounterId : undefined);
      return;
    }
    const match = appointmentToPreselect(
      appointments,
      transmissionTasks?.items ?? [],
      reportId,
      appointment?.id,
      new Date(),
      inClinicOnly
    );
    if (!match) return;
    const source = appointments.find((item) => item.id === match.id) ?? null;
    handleChange({ value: match.id, label: getAppointmentName(match) }, source);
  }, [
    appointment?.id,
    appointments,
    handleChange,
    inClinicOnly,
    isDisabled,
    isLoading,
    onCurrentAppointmentEncounter,
    reportId,
    savedAppointmentId,
    taskTypesLoaded,
    transmissionTaskTypeId,
    transmissionTasks
  ]);

  const selectedAppointment = useMemo(() => {
    if (!appointment?.id) return { value: '', label: '-' };
    const fromOptions = options.find((option) => option.value === appointment.id);
    if (fromOptions) return fromOptions;
    const source =
      rememberedAppointment.current?.id === appointment.id
        ? rememberedAppointment.current
        : appointment;
    return {
      value: appointment.id,
      label: getAppointmentName(source)
    };
  }, [options, appointment]);

  const handleAppointmentChange = (option: AppointmentOption | null) => {
    if (!option?.value) {
      handleChange(null, null);
      return;
    }
    const source = appointments?.find((item) => item.id === option.value) ?? null;
    if (source) rememberedAppointment.current = source;
    handleChange(option, source);
  };

  return (
    <div>
      <Dropdown
        options={options}
        onChange={handleAppointmentChange}
        isMulti={false}
        value={selectedAppointment}
        placeholder={isLoading ? 'Loading appointments...' : 'Select an appointment'}
        isDisabled={isLoading || isDisabled || lockedToTask}
        customComponents={{ Option: CustomOption }}
      />
      {requireSelection && !appointment?.id && !isDisabled && (
        <Text c="red" size="xs" mt={4}>
          Select an appointment to sign the report.
        </Text>
      )}
    </div>
  );
};
